/**
 * IP address text: validation, the one canonical spelling, and URL form.
 *
 * The backend stores every address in one spelling (`db::ip_text` there):
 * what Rust's `IpAddr` prints, RFC 5952 for IPv6. `canonicalIp` produces the
 * same text, so a search or comparison typed in another spelling still matches.
 */

/** The four octets of a dotted-decimal IPv4 address, or null. Leading zeros are rejected (`010` is octal to some parsers). */
function parseIpv4(v: string): number[] | null {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(v);
  if (!m) return null;
  const octets = m.slice(1).map(Number);
  const ok = m.slice(1).every((oct, i) => octets[i] <= 255 && String(octets[i]) === oct);
  return ok ? octets : null;
}

/**
 * The eight 16-bit groups of an IPv6 address, or null. Accepts `::` for one
 * run of zero groups and a dotted IPv4 tail (`::ffff:192.0.2.10`). No zone
 * (`%eth0`) and no brackets.
 */
function parseIpv6(v: string): number[] | null {
  if (!/^[0-9a-fA-F:.]+$/.test(v) || !v.includes(':')) return null;
  const halves = v.split('::');
  if (halves.length > 2) return null;

  const groupsOf = (part: string, mayEndWithIpv4: boolean): number[] | null => {
    if (part === '') return [];
    const out: number[] = [];
    const pieces = part.split(':');
    for (let i = 0; i < pieces.length; i++) {
      const piece = pieces[i];
      if (piece.includes('.')) {
        const v4 = mayEndWithIpv4 && i === pieces.length - 1 ? parseIpv4(piece) : null;
        if (!v4) return null;
        out.push((v4[0] << 8) | v4[1], (v4[2] << 8) | v4[3]);
      } else {
        if (!/^[0-9a-fA-F]{1,4}$/.test(piece)) return null;
        out.push(parseInt(piece, 16));
      }
    }
    return out;
  };

  if (halves.length === 1) {
    const groups = groupsOf(v, true);
    return groups && groups.length === 8 ? groups : null;
  }
  const head = groupsOf(halves[0], false);
  const tail = groupsOf(halves[1], true);
  if (!head || !tail) return null;
  const missing = 8 - head.length - tail.length;
  // `::` stands for at least one zero group.
  if (missing < 1) return null;
  return [...head, ...Array<number>(missing).fill(0), ...tail];
}

/** RFC 5952 text for eight groups: lowercase, no leading zeros, the longest run (2+) of zero groups as `::`, the first if tied. */
function formatIpv6(groups: number[]): string {
  let bestStart = -1;
  let bestLen = 0;
  for (let i = 0; i < 8; ) {
    if (groups[i] !== 0) {
      i++;
      continue;
    }
    let j = i;
    while (j < 8 && groups[j] === 0) j++;
    if (j - i > bestLen) {
      bestStart = i;
      bestLen = j - i;
    }
    i = j;
  }
  const hex = groups.map((g) => g.toString(16));
  if (bestLen < 2) return hex.join(':');
  return `${hex.slice(0, bestStart).join(':')}::${hex.slice(bestStart + bestLen).join(':')}`;
}

/** Whether `value` is an IPv4 or IPv6 address, as a device config would take it (no brackets, no zone). */
export function isValidIpAddress(value: string): boolean {
  const v = value.trim();
  return parseIpv4(v) !== null || parseIpv6(v) !== null;
}

/**
 * The spelling the backend stores for an address, or null when `value` is not
 * one. Brackets are accepted, and an IPv4-mapped address (`::ffff:a.b.c.d`) is
 * the IPv4 address.
 */
export function canonicalIp(value: string): string | null {
  let v = value.trim();
  if (v.startsWith('[') && v.endsWith(']')) v = v.slice(1, -1);
  const v4 = parseIpv4(v);
  if (v4) return v4.join('.');
  const v6 = parseIpv6(v);
  if (!v6) return null;
  const mapped = v6.slice(0, 5).every((g) => g === 0) && v6[5] === 0xffff;
  if (mapped) return [v6[6] >> 8, v6[6] & 0xff, v6[7] >> 8, v6[7] & 0xff].join('.');
  return formatIpv6(v6);
}

/** An address family an operation variable can be limited to. */
export type AddressFamily = 'ipv4' | 'ipv6';

function familyName(family: AddressFamily | null | undefined): string {
  return family === 'ipv4' ? 'IPv4' : family === 'ipv6' ? 'IPv6' : 'IPv4 or IPv6';
}

/** Why `value` is not an address of `family` (either family when absent), or null when it is. */
export function addressProblem(value: string, family?: AddressFamily | null): string | null {
  const v = value.trim();
  const v4 = parseIpv4(v) !== null;
  const v6 = !v4 && parseIpv6(v) !== null;
  if ((!v4 && !v6) || (family === 'ipv6' && v4) || (family === 'ipv4' && v6)) {
    return `Enter a valid ${familyName(family)} address.`;
  }
  return null;
}

/**
 * Why `value` is not a network prefix (`address/length`) of `family`, or null
 * when it is. The same rules the gateway applies: a length within the family,
 * and no bits set past it.
 */
export function prefixProblem(value: string, family?: AddressFamily | null): string | null {
  const v = value.trim();
  const slash = v.indexOf('/');
  const example = family === 'ipv4' ? '10.70.0.0/16' : '2001:db8:70::/64';
  const v4 = slash < 0 ? null : parseIpv4(v.slice(0, slash));
  const v6 = slash < 0 || v4 ? null : parseIpv6(v.slice(0, slash));
  const lengthText = v.slice(slash + 1);
  if ((!v4 && !v6) || !/^\d{1,3}$/.test(lengthText)) {
    return `Enter a ${familyName(family)} prefix with its length, e.g. ${example}.`;
  }
  if ((family === 'ipv6' && v4) || (family === 'ipv4' && v6)) {
    return `Enter a ${familyName(family)} prefix, e.g. ${example}.`;
  }
  const [words, width] = v4 ? [v4, 8] : [v6 ?? [], 16];
  const length = Number(lengthText);
  if (length > words.length * width) {
    return `A ${v4 ? 'IPv4' : 'IPv6'} prefix length is at most ${String(words.length * width)}.`;
  }
  for (let i = 0; i < words.length; i++) {
    const kept = Math.min(Math.max(length - i * width, 0), width);
    const hostBits = kept >= width ? 0 : (1 << (width - kept)) - 1;
    if ((words[i] & hostBits) !== 0) {
      return `Bits are set past the /${lengthText} length: this names a host, not a network.`;
    }
  }
  return null;
}

/** A block of IPv6 space, by its first three groups' bits, and what it is. */
interface Block {
  groups: [number, number, number];
  length: number;
  what: string;
}

function inBlock(groups: number[], block: Block): boolean {
  for (let i = 0; i < 3; i++) {
    const kept = Math.min(Math.max(block.length - i * 16, 0), 16);
    const mask = kept === 0 ? 0 : (0xffff << (16 - kept)) & 0xffff;
    if ((groups[i] & mask) !== block.groups[i]) return false;
  }
  return true;
}

const blockText = (b: Block): string => `${formatIpv6([...b.groups, 0, 0, 0, 0, 0])}/${String(b.length)}`;

// The space an EUI-64 address may be made in, as vendor-profiles' `eui64`
// module has it (IANA's address-space and special-purpose registries).
const EUI64_ALLOWED: Block[] = [
  { groups: [0x2000, 0, 0], length: 3, what: 'global unicast' },
  { groups: [0xfd00, 0, 0], length: 8, what: 'unique local' },
];
const EUI64_SET_ASIDE: Block[] = [
  {
    groups: [0x2001, 0, 0],
    length: 23,
    what: 'set aside for IETF protocols (Teredo, ORCHID, AMT, benchmarking and others)',
  },
  { groups: [0x2620, 0x4f, 0x8000], length: 48, what: "the AS112 DNS service's" },
];
const EUI64_ELSEWHERE: Block[] = [
  {
    groups: [0xfc00, 0, 0],
    length: 8,
    what: 'the undefined half of unique local space; a unique local address is made in fd00::/8',
  },
  { groups: [0xfe80, 0, 0], length: 10, what: 'link-local; the device makes those addresses itself' },
  { groups: [0xfec0, 0, 0], length: 10, what: 'the deprecated site-local block' },
  { groups: [0xff00, 0, 0], length: 8, what: 'multicast' },
  { groups: [0, 0, 0], length: 8, what: 'reserved (loopback, unspecified, IPv4-mapped and translation addresses)' },
  { groups: [0x0100, 0, 0], length: 8, what: 'reserved (the discard-only block)' },
  { groups: [0x5f00, 0, 0], length: 16, what: 'for SRv6 segment identifiers' },
];

/**
 * What an EUI-64 prefix typed as `value` is stored as — `fd00:1:2:3::` and
 * `FD00:1:2:3::/64` are both `fd00:1:2:3::/64` — and why not, when it can't be.
 * The same rules the backend parses by (vendor-profiles `eui64::parse`).
 */
export function eui64Prefix(value: string): { value: string; problem: null } | { value: null; problem: string } {
  const refuse = (problem: string) => ({ value: null, problem });
  const typed = value.trim();
  const slash = typed.indexOf('/');
  const address = (slash < 0 ? typed : typed.slice(0, slash)).trim();
  const length = slash < 0 ? null : typed.slice(slash + 1).trim();
  const groups = parseIpv6(address);
  if (!groups) return refuse('Enter an IPv6 network, e.g. fd00:1:2:3::');
  if (length !== null && (!/^\d{1,3}$/.test(length) || Number(length) !== 64)) {
    return refuse(`An EUI-64 address is made in a /64, not a /${length}.`);
  }
  const network = `${formatIpv6([...groups.slice(0, 4), 0, 0, 0, 0])}/64`;
  if (groups.slice(4).some((g) => g !== 0)) {
    return refuse(`That's an address in ${network}, not the /64 itself.`);
  }
  const aside = EUI64_SET_ASIDE.find((b) => inBlock(groups, b));
  if (aside) return refuse(`${network} is in ${blockText(aside)}, ${aside.what} space.`);
  if (!EUI64_ALLOWED.some((b) => inBlock(groups, b))) {
    const where = EUI64_ELSEWHERE.find((b) => inBlock(groups, b));
    const what = where ? `${blockText(where)}, ${where.what}` : 'space the IETF reserves';
    return refuse(
      `${network} is in ${what}; an EUI-64 address is made in global unicast (2000::/3) or unique local (fd00::/8) space.`,
    );
  }
  return { value: network, problem: null };
}

/**
 * Why `value` is not an interface address (`address/length`, host bits and
 * all) of `family`, or null when it is. The same rules the backend parses by.
 */
export function interfaceAddressProblem(value: string, family?: AddressFamily | null): string | null {
  const v = value.trim();
  const slash = v.indexOf('/');
  const example = family === 'ipv6' ? '2001:db8::1/64' : '192.0.2.1/24';
  const address = slash < 0 ? '' : v.slice(0, slash);
  const lengthText = slash < 0 ? '' : v.slice(slash + 1);
  const v4 = parseIpv4(address) !== null;
  const v6 = !v4 && parseIpv6(address) !== null;
  if ((!v4 && !v6) || !/^\d{1,3}$/.test(lengthText)) {
    return `Enter a ${familyName(family)} address with its length, e.g. ${example}.`;
  }
  if ((family === 'ipv6' && v4) || (family === 'ipv4' && v6)) {
    return `Enter a ${familyName(family)} address, e.g. ${example}.`;
  }
  const most = v4 ? 32 : 128;
  if (Number(lengthText) > most) {
    return `A ${v4 ? 'IPv4' : 'IPv6'} prefix length is at most ${String(most)}.`;
  }
  return null;
}

/**
 * Whether an operation variable's value is a whole number: an `integer`, an
 * `mtu` (the largest IP packet, in bytes), or a VLAN by its id (`vlan`, one the
 * device has; `vlan_id`, one being created), whose 1-4094 bounds come with its
 * type.
 */
export function isWholeNumberVariable(varType: string): boolean {
  return varType === 'integer' || varType === 'mtu' || varType === 'vlan' || varType === 'vlan_id';
}

/** Whether an operation variable's type is one of the address types checked here. */
export function isAddressVariable(varType: string): boolean {
  return (
    varType === 'ip_address' || varType === 'prefix' || varType === 'eui64_prefix' || varType === 'interface_address'
  );
}

/**
 * The problem with an address-typed operation variable's value, or null when
 * it is fine or the variable is not address-typed.
 */
export function addressVariableProblem(
  varType: string,
  value: string,
  family?: AddressFamily | null,
): string | null {
  if (varType === 'ip_address') return addressProblem(value, family);
  if (varType === 'prefix') return prefixProblem(value, family);
  if (varType === 'eui64_prefix') return eui64Prefix(value).problem;
  if (varType === 'interface_address') return interfaceAddressProblem(value, family);
  return null;
}

/** A host as it goes into a URL: an IPv6 address in brackets, anything else unchanged. */
export function hostForUrl(host: string): string {
  return host.includes(':') && !host.startsWith('[') ? `[${host}]` : host;
}
